import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { message } from "antd";
import logoIcon from "../assets/logo-icon.png";

import { useLocation } from "react-router-dom";
import useSWRMutation from "swr/mutation";
import { handleMutation } from "../util/axiosConfig";
import { API_ENDPOINTS } from "../util/api";
import { Password } from "../components/ui/password";
import useAuth from "../hooks/useAuth";

// Validation schema
const setPasswordSchema = z
  .object({
    password: z.string().min(6, "Password must be at least 6 characters"),
    confirmPassword: z.string().min(6, "Confirm password must be at least 6 characters"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type SetPasswordFormValues = z.infer<typeof setPasswordSchema>;

export default function AdminSetPassword() {
  const { logOut } = useAuth();
  const {trigger, isMutating} = useSWRMutation(API_ENDPOINTS.ADMIN.SET_PASSWORD, handleMutation)

const location = useLocation();
const queryParams = new URLSearchParams(location.search);
const token = queryParams.get("token");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SetPasswordFormValues>({
    resolver: zodResolver(setPasswordSchema),
  });

  const onSubmit = async (data: SetPasswordFormValues) => {
    try {
      await trigger({ password: data.password, token })
      message.success("Password updated successfully");
      logOut();
    } catch (error: any) {
      message.error(error.message);
    } 
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md space-y-8">
        <div className="flex justify-center">
          <img src={logoIcon} alt="Logo" />
        </div>

        <h2 className="mt-6 text-center text-xl font-semibold text-gray-900">
          Set your new password.
        </h2>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-6">
            {/* New Password */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                New password*
              </Label>
              <div className="flex">
                <div className="w-1 bg-blue-600" />
                <Password
                  id="password"
                  type="password"
                  placeholder="New password"
                  className="flex-1 bg-gray-200 border-0 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 placeholder-gray-500"
                  disabled={isMutating}
                  {...register("password")}
                />
              </div>
              {errors.password && (
                <p className="text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>

            {/* Confirm New Password */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
                Confirm new password*
              </Label>
              <div className="flex">
                <div className="w-1 bg-blue-600" />
                <Password
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm password"
                  className="flex-1 bg-gray-200 border-0 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 placeholder-gray-500"
                  disabled={isMutating}
                  {...register("confirmPassword")}
                />
              </div>
              {errors.confirmPassword && (
                <p className="text-sm text-red-600">{errors.confirmPassword.message}</p>
              )}
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-none"
            disabled={isMutating}
          >
            {isMutating ? "Submitting..." : "Set Password"}
          </Button>
        </form>
      </div>
    </div>
  );
}
